/**
 * TypingIndicator — animated "FishMemory is typing..." indicator.
 */

import React, { useEffect, useRef, memo, useState } from 'react';
import { View, Text, Animated, StyleSheet, AccessibilityInfo } from 'react-native';
import { useTheme } from '../../utils/theme';
import { SPACING, RADIUS } from '../../utils/design-tokens';

interface Props {
  visible: boolean;
}

function TypingIndicator({ visible }: Props) {
  const { colors } = useTheme();
  const dot1 = useRef(new Animated.Value(0)).current;
  const dot2 = useRef(new Animated.Value(0)).current;
  const dot3 = useRef(new Animated.Value(0)).current;
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    AccessibilityInfo.isReduceMotionEnabled().then(setReduceMotion);
  }, []);

  useEffect(() => {
    if (!visible || reduceMotion) return;

    const animate = (dot: Animated.Value, delay: number) =>
      Animated.loop(
        Animated.sequence([
          Animated.delay(delay),
          Animated.timing(dot, {
            toValue: 1,
            duration: 400,
            useNativeDriver: true,
          }),
          Animated.timing(dot, {
            toValue: 0,
            duration: 400,
            useNativeDriver: true,
          }),
        ])
      );

    const a1 = animate(dot1, 0);
    const a2 = animate(dot2, 200);
    const a3 = animate(dot3, 400);

    a1.start();
    a2.start();
    a3.start();

    return () => {
      a1.stop();
      a2.stop();
      a3.stop();
    };
  }, [visible]);

  if (!visible) return null;

  const dotStyle = (anim: Animated.Value) => ({
    opacity: anim.interpolate({
      inputRange: [0, 1],
      outputRange: [0.3, 1],
    }),
    transform: [
      {
        translateY: anim.interpolate({
          inputRange: [0, 1],
          outputRange: [0, -4],
        }),
      },
    ],
  });

  return (
    <View style={styles.container}>
      <View style={[styles.bubble, { backgroundColor: colors.card, borderColor: colors.border }]}>
        <Animated.View style={[styles.dot, { backgroundColor: colors.textSecondary }, dotStyle(dot1)]} />
        <Animated.View style={[styles.dot, { backgroundColor: colors.textSecondary }, dotStyle(dot2)]} />
        <Animated.View style={[styles.dot, { backgroundColor: colors.textSecondary }, dotStyle(dot3)]} />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.xs,
    alignItems: 'flex-start',
  },
  bubble: {
    flexDirection: 'row',
    borderRadius: RADIUS.lg,
    borderTopLeftRadius: RADIUS.xs,
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.md,
    gap: SPACING.xs,
    borderWidth: 1,
  },
  dot: {
    width: SPACING.sm,
    height: SPACING.sm,
    borderRadius: SPACING.xs,
  },
});

export default memo(TypingIndicator);
